import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CalendarOptions, EventInput } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import resourceTimeGridPlugin from "@fullcalendar/resource-timegrid";
import resourceDayGridPlugin from '@fullcalendar/resource-daygrid';
import rrulePlugin from '@fullcalendar/rrule';

import tippy from 'tippy.js';
import { FullCalendarComponent } from '@fullcalendar/angular';
import { ModalComponent } from '../../modals/modal/modal.component';
import { ToastService } from 'src/app/modules/core/services/toast.service';
import { Subject } from 'rxjs';
import { CalendarSchedule } from 'src/app/pages/main/_models/Monitoring/Schedules';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { OverrideModalComponent } from './override-modal/override-modal.component';

@Component({
    selector: 'app-fullcalendar',
    templateUrl: './fullcalendar.component.html',
    standalone: false
})
export class FullcalendarComponent implements OnInit, OnChanges, OnDestroy {

  @ViewChild('deleteEvent') deleteEventModalComponent: ModalComponent;
  @ViewChild('addEvent') addEventModalComponent: ModalComponent;
  @ViewChild(FullCalendarComponent, { static: true }) calendar: FullCalendarComponent;

  @Input() siteId: number;
  @Input() events: EventInput[];
  @Input() calendarSchedules: CalendarSchedule[];
  @Input() adjustedCalendarSchedules: CalendarSchedule[];
  @Input() resources: any[];
  @Input() resourceAreas: any[];
  @Input() height: number;
  @Input() headerOptions: {};
  @Input() views: {};
  @Input() initialView: string;
  @Input() selectedCustomerName: string;
  @Input() selectedSiteName: string;
  @Input() selectedNvrName: string;

  @Input() isGuardTour: boolean;

  @Output() eventDeleted: EventEmitter<boolean> = new EventEmitter();

  calendarOptions: CalendarOptions;

  public _isOverride: boolean;

  public originalStartDate: Date = new Date();
  public originalEndDate: Date = new Date();

  protected _onDestroy = new Subject();

  constructor(
    private modalService: NgbModal,
    private toastService: ToastService
  ) { }

  ngOnInit() {
    this.init();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.resourceAreas?.previousValue !== undefined) {
      //update the header columns
      this.calendar.getApi().setOption('resourceAreaColumns', this.resourceAreas)
      //update the resources
      this.calendar.getApi().refetchResources();
      //update the events
      this.calendar.getApi().removeAllEventSources();
      this.calendar.getApi().addEventSource(changes.events?.currentValue);
      this.calendar.getApi().refetchEvents();
    }
  }

  init() {
    //get UTC Date today
    const TODAY_STR = new Date().toISOString().replace(/T.*$/, '');

    this.calendarOptions = {
      schedulerLicenseKey: '0211306109-fcs-1786131304',
      initialView: this.initialView,
      stickyFooterScrollbar: true,
      height: this.height,
      eventStartEditable: false,
      editable: true,
      aspectRatio: 1.5,
      initialDate: TODAY_STR,
      resourceAreaWidth: '40%',
      resourceAreaColumns: this.resourceAreas,
      resources: this.resources,
      plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, resourceTimelinePlugin, resourceTimeGridPlugin, resourceDayGridPlugin, rrulePlugin],
      nowIndicator: true,
      eventMouseEnter: function (info) {
        tippy(info.el, {
          theme: 'light-border',
          allowHTML: true,
          content: info.event.display,
          placement: 'top',
          popperOptions: {
            modifiers: [
              {
                name: 'flip',
                options: {
                  fallbackPlacements: ['bottom', 'right', 'left'],
                },
              },
            ],
          },
        });
      },
      headerToolbar: this.headerOptions,
      titleFormat: { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' },
      events: this.events,
      eventContent: function (info) {
        return { html: `${info.event.title}` };
      },
      eventClick: this.overrideEvent.bind(this),
      views: this.views
    };
  }

  overrideEvent(info?: any) {
    if (info.event._def.recurringDef == null && this.adjustedCalendarSchedules) {
      this._isOverride = false;
      //find the schedule that needs to be overridden
      let calSchedOverride = this.adjustedCalendarSchedules.find(sched => sched.id == Number(info.event.groupId));
      this.originalStartDate = info.event.start;
      this.originalEndDate = info.event.end;

      const modalRef = this.modalService.open(OverrideModalComponent, { size: 'md', centered: true });

      modalRef.componentInstance.calendarSchedules = this.calendarSchedules;
      modalRef.componentInstance._isOverride = this._isOverride;
      modalRef.componentInstance.originalStartDate = this.originalStartDate;
      modalRef.componentInstance.originalEndDate = this.originalEndDate;
      modalRef.componentInstance.calSchedOverride = calSchedOverride;
      modalRef.componentInstance.selectedCustomerName = this.selectedCustomerName;
      modalRef.componentInstance.selectedSiteName = this.selectedSiteName;
      modalRef.componentInstance.selectedNvrName = this.selectedNvrName;

      modalRef.result.then((res) => {
        if (res) {
          //Reload the Calendar
          this.eventDeleted.emit(true);
        }
      });
    }
    else {
      this._isOverride = true;
    }
  }

  getEvents() {
    return this.calendar.getApi().getEvents();
}

  ngOnDestroy(): void {
    this._onDestroy.complete();
  }

}